<script lang="ts" setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useI18n } from 'vue-i18n'
import Popper from 'vue3-popper'
import {
  DBtn,
  DTextField,
  DTable,
  DModal,
  DLoading,
  Trash03Icon,
  Lock03Icon,
  LockUnlocked03Icon
} from 'front-library'
import {
  getAPIKeys,
  stateApiKey,
  deleteApiKey
} from '@/app/core/services/api-key-management'
usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const router = useRouter()
const store = useUserStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal y Loading */
const showModal = ref(false)
const titleModal = ref('')
const descriptionModal = ref('')
const acceptQuestion = ref('')
const typeAlertModal = ref('')

const loading = ref(false)
const messageLoading = ref('')

/* Datos */
const search = ref()
const tableApiKey = ref(null)
const apiKeyForDelete = ref()
const apiKeyUpdateState = ref()
const isDelete = ref(false)
const urlFile = ref(null)

/* Datos componente table */
const columns = ref([
  { name: 'name', title: t('apiKeys.list.name'), table: 'name' },
  { name: 'state', title: t('apiKeys.list.state'), table: 'active' },
  { name: 'namePayrolls', title: t('apiKeys.list.payrolls') },
  { name: 'nameRol', title: t('apiKeys.list.role') },
  {
    name: 'expires_at',
    title: t('apiKeys.list.dueDate'),
    table: 'expires_at'
  },
  { name: 'actions', title: '' }
])
const rows = ref<[]>([])
const perPage = ref(0)
const offset = ref(0)
const totalRows = ref(0)
const sortBy = ref('')

/* Redirect's */
const createApiKey = () => {
  router.push({ name: 'CreateApiKey' })
}

/* Cambiar estado de la apiKey */
const changeStateApiKey = (values: any) => {
  typeAlertModal.value = 'question'
  const text = !values.active
    ? t('table.action.activate')
    : t('table.action.deactivate')
  apiKeyUpdateState.value = [
    { name: values.name, id: values.id, is_inactive: values.active }
  ]
  titleModal.value = t('apiKeys.list.actionStatus', {
    status: text,
    apikey: values.name as string
  })
  descriptionModal.value = ''
  acceptQuestion.value = t('modal.acceptQuestion', { action: text })
  showModal.value = true
  isDelete.value = false
}
const changeStateConfirm = async () => {
  loading.value = true
  showModal.value = false
  await stateApiKey(
    apiKeyUpdateState.value[0].id,
    !apiKeyUpdateState.value[0].is_inactive
  )
    .then(response => {
      notifications.addNotification({
        title: apiKeyUpdateState.value[0].is_inactive
          ? t('notification.disabled')
          : t('notification.activated'),
        text: t('apiKeys.notification.changeStatus', {
          apikey: apiKeyUpdateState.value[0].name as string,
          status: apiKeyUpdateState.value[0].is_inactive
            ? t('table.action.deactivated')
            : t('table.action.asset')
        }),
        type: 'success',
        time: 10000
      })
      void apiKeys()
    })
    .catch((error: any) => {
      notificationError(error, 'apiKeys', t, notifications, loading)
    })
}

/* Eliminar apikey */
const removeApiKey = (values: any) => {
  typeAlertModal.value = 'question'
  titleModal.value = t('apiKeys.list.actionStatus', {
    status: t('table.action.delete'),
    apikey: values.name as string
  })
  descriptionModal.value = t('modal.description')
  acceptQuestion.value = t('modal.acceptQuestion', {
    action: t('table.action.delete')
  })
  showModal.value = true
  apiKeyForDelete.value = values.id
  isDelete.value = true
}
const deleteConfirm = async () => {
  loading.value = true
  showModal.value = false
  await deleteApiKey(apiKeyForDelete.value)
    .then(response => {
      loading.value = false
      notifications.addNotification({
        title: t('notification.removed'),
        text: t('apiKeys.notification.action', [t('notification.wasRemoved')]),
        type: 'success',
        time: 10000
      })
      void apiKeys()
    })
    .catch(async (error: any) => {
      notificationError(error, 'apiKeys', t, notifications, loading)
    })
}

/* Cerrar el componente Modal */
const closeModal = () => {
  showModal.value = false
  urlFile.value = null
}

/* Listar direcciones */
const apiKeys = async () => {
  loading.value = true
  messageLoading.value = t('loading.data')
  try {
    const data: any = await getAPIKeys(
      offset.value,
      perPage.value,
      sortBy.value,
      search.value === '' ? undefined : search.value
    )
    rows.value = data.data
    totalRows.value = data.totalItems
    loading.value = false
  } catch (error: any) {
    notificationError(error, 'apiKeys', t, notifications, loading)
  }
}
</script>
<template>
  <DLoading v-show="loading" :message="messageLoading" />
  <DModal
    :open="showModal"
    :typeAlert="typeAlertModal"
    :title="titleModal"
    :description="descriptionModal"
    :acceptQuestion="acceptQuestion"
    @closeModal="closeModal"
    @otherMethod="isDelete ? deleteConfirm() : changeStateConfirm()">
    <template v-slot:description v-if="urlFile != null">
      <a
        class="underline tracking-widest text-blue-600"
        :href="urlFile"
        target="_blank"
        v-html="t('reports.notification.viewFile')" />
    </template>
  </DModal>
  <div class="h-full px-5 overflow-auto scroll">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow"
          v-html="t('apiKeys.list.title', 1)" />
        <p
          class="text-gray-500 text-shadow"
          v-html="t('apiKeys.list.description')" />
      </div>
      <div class="sm:flex justify-between pt-8">
        <div class="flex mb-3 sm:mb-0 max-w-2xl sm:w-2/3 md:w-1/2 lg:w-5/12">
          <DTextField
            :placeholder="t('form.button.search') + '...'"
            :icon="'SearchLgIcon'"
            class="mr-2 w-full"
            v-model="search"
            @keyup.enter="apiKeys" />
          <DBtn @click.prevent="apiKeys" v-html="t('form.button.search')" />
        </div>
        <div class="flex gap-2">
          <DBtn
            :icon="'PlusIcon'"
            type="button"
            @click="createApiKey()"
            v-if="store.hasPermission('apikeys:create')">
            {{ t('form.button.create', { form: t('apiKeys.list.title', 0) }) }}
          </DBtn>
        </div>
      </div>
    </div>
    <DTable
      ref="tableApiKey"
      :columns="columns"
      :rows="rows"
      :total-rows="totalRows"
      :select-options="false"
      :text-no-data="t('table.noData')"
      :text-per-page="t('table.rowsPerPage')"
      @perPage=";(perPage = $event), apiKeys()"
      @offset="offset = $event"
      @sort=";(sortBy = $event), apiKeys()">
      <template #state="data: any">
        <div
          class="flex items-center rounded-full font-semibold w-fit text-sm py-1 px-3"
          :class="
            !data.active
              ? 'bg-red-100 text-red-600'
              : 'bg-green-100 text-green-600'
          ">
          <div
            class="mr-2 w-1.5 h-1.5 rounded-full"
            :class="!data.active ? 'bg-red-600' : 'bg-green-600'"></div>
          {{
            !data.active ? t('table.action.inactive') : t('table.action.active')
          }}
        </div>
      </template>
      <template #nameRol="data: any">
        <div>
          {{ data.rol['name'] }}
        </div>
      </template>
      <template #namePayrolls="data: any">
        <div
          class="max-w-xs overflow-hidden whitespace-pre-line text-ellipsis flex break-words justify-start"
          style="inline-size: max-content">
          {{ data?.payrolls.map((_: any) => _.code).join(', ') }}
        </div>
      </template>
      <template #actions="data: any">
        <div class="flex py-2.5 justify-end gap-1">
          <Popper
            v-if="store.hasPermission('apikeys:enable')"
            arrow
            offsetDistance="12"
            :content="
              !data.active
                ? t('apiKeys.list.activate')
                : t('apiKeys.list.deactivate')
            "
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="changeStateApiKey(data)">
              <Lock03Icon
                v-if="!data.active"
                class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
              <LockUnlocked03Icon
                v-else
                class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
          <Popper
            v-if="!data.active && store.hasPermission('apikeys:delete')"
            arrow
            offsetDistance="12"
            :content="t('table.action.remove')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div class="bg-gray-50 rounded-md py-2" @click="removeApiKey(data)">
              <Trash03Icon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
        </div>
      </template>
    </DTable>
  </div>
</template>
